import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Slider } from '../../components/ui/slider';
import { toast } from 'sonner';
import { ShieldCheck, Save, RefreshCw, AlertTriangle } from 'lucide-react';
import { apiGet, apiPut, BOT_LABELS, BOT_COLORS } from './ops_helpers';

const RISK_BOTS = ['hyperliquid', 'ftmo'];
const MIN_PCT = 0.1;
const MAX_PCT = 5.0; // tope de seguridad (coincide con el cap del backend)

// fraccion (0.01) -> % (1.0)
const toPct = (frac) => Math.round((Number(frac) || 0) * 1000) / 10;

/**
 * Panel de Riesgo por bot: un deslizador simple (0.1%–5%) para ajustar el
 * risk_per_trade_pct de cada bot en caliente. Versiona el cambio y el bot lo
 * aplica en su proximo ciclo de polling. Tope duro de 5% (front + backend).
 */
export function RiskControlPanel({ isAdmin }) {
  const [state, setState] = useState({}); // { [bot]: {pct, savedPct, version, params, loading, saving, err} }

  const loadBot = useCallback(async (bot) => {
    setState((s) => ({ ...s, [bot]: { ...(s[bot] || {}), loading: true, err: null } }));
    try {
      const data = await apiGet(`/api/ops/config/${bot}/admin`);
      const cfg = data?.active_config || null;
      const params = cfg?.params || {};
      const frac = params.risk_per_trade_pct;
      const pct = frac != null ? Math.min(Math.max(toPct(frac), MIN_PCT), MAX_PCT) : null;
      setState((s) => ({
        ...s,
        [bot]: {
          loading: false, saving: false, err: null,
          params, version: cfg?.version ?? null,
          pct: pct ?? 1.0, savedPct: pct, hasConfig: !!cfg,
        },
      }));
    } catch (e) {
      setState((s) => ({ ...s, [bot]: { ...(s[bot] || {}), loading: false, err: e.message } }));
    }
  }, []);

  useEffect(() => { RISK_BOTS.forEach(loadBot); }, [loadBot]);

  const setPct = (bot, val) => {
    const pct = Math.min(Math.max(Number(val) || 0, MIN_PCT), MAX_PCT);
    setState((s) => ({ ...s, [bot]: { ...(s[bot] || {}), pct } }));
  };

  const save = async (bot) => {
    const b = state[bot];
    if (!b) return;
    const pct = Math.min(Math.max(b.pct, MIN_PCT), MAX_PCT);
    setState((s) => ({ ...s, [bot]: { ...s[bot], saving: true } }));
    try {
      const params = { ...(b.params || {}), risk_per_trade_pct: Math.round((pct / 100) * 100000) / 100000 };
      await apiPut(`/api/ops/config/${bot}`, {
        params,
        note: `Riesgo ajustado a ${pct.toFixed(1)}% desde el panel`,
        activate: true,
      });
      toast.success(`${BOT_LABELS[bot] || bot}: riesgo → ${pct.toFixed(1)}%`, {
        description: 'El bot lo aplicará en su próximo ciclo (unos segundos/minutos).',
      });
      await loadBot(bot);
    } catch (e) {
      toast.error(`No se pudo guardar: ${e.message}`);
      setState((s) => ({ ...s, [bot]: { ...s[bot], saving: false } }));
    }
  };

  return (
    <Card className="border-zinc-800 bg-zinc-950/60" data-testid="risk-control-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-zinc-100">
          <ShieldCheck className="h-5 w-5 text-amber-400" /> Riesgo por Bot
        </CardTitle>
        <CardDescription className="text-zinc-400">
          Ajusta el riesgo por operación de cada bot. Se aplica en caliente (el bot lo
          recoge en su siguiente ciclo). Tope de seguridad: <b className="text-amber-400">5%</b>.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2">
        {RISK_BOTS.map((bot) => {
          const b = state[bot] || {};
          const color = BOT_COLORS[bot] || '#f59e0b';
          const dirty = b.savedPct == null || Math.abs((b.pct ?? 0) - (b.savedPct ?? -1)) > 0.05;
          return (
            <div
              key={bot}
              className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4"
              data-testid={`risk-card-${bot}`}
            >
              <div className="mb-3 flex items-center justify-between">
                <span className="font-medium text-zinc-100" style={{ color }}>
                  {BOT_LABELS[bot] || bot}
                </span>
                {b.savedPct != null ? (
                  <Badge className="bg-zinc-800 text-zinc-200" data-testid={`risk-current-${bot}`}>
                    actual: {b.savedPct.toFixed(1)}%
                  </Badge>
                ) : (
                  <Badge className="bg-amber-900/60 text-amber-300 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> sin config
                  </Badge>
                )}
              </div>

              {b.loading ? (
                <div className="py-4 text-center text-sm text-zinc-500">Cargando…</div>
              ) : (
                <>
                  <div className="mb-2 flex items-end gap-3">
                    <span
                      className="text-3xl font-bold tabular-nums"
                      style={{ color }}
                      data-testid={`risk-value-${bot}`}
                    >
                      {(b.pct ?? 0).toFixed(1)}%
                    </span>
                    <span className="pb-1 text-xs text-zinc-500">por operación</span>
                  </div>

                  <Slider
                    value={[b.pct ?? MIN_PCT]}
                    min={MIN_PCT}
                    max={MAX_PCT}
                    step={0.1}
                    onValueChange={(v) => setPct(bot, v[0])}
                    className="my-3"
                    data-testid={`risk-slider-${bot}`}
                  />
                  <div className="mb-3 flex justify-between text-[10px] text-zinc-600">
                    <span>0.1%</span><span>2.5%</span><span>5%</span>
                  </div>

                  <Button
                    onClick={() => save(bot)}
                    disabled={!isAdmin || b.saving || !dirty}
                    className="w-full bg-amber-600 text-zinc-950 hover:bg-amber-500"
                    data-testid={`risk-save-${bot}`}
                  >
                    {b.saving ? (
                      <><RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Guardando…</>
                    ) : (
                      <><Save className="mr-2 h-4 w-4" /> Aplicar {(b.pct ?? 0).toFixed(1)}%</>
                    )}
                  </Button>
                  {b.err && <p className="mt-2 text-xs text-red-400">{b.err}</p>}
                  {!isAdmin && <p className="mt-2 text-xs text-zinc-500">Solo administradores.</p>}
                </>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default RiskControlPanel;
